import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// World brand mark through the local server's grant door (LG4): the same
// worldBrandLogos fold the World authority holds. The browser names a World
// and a member id; the Actor and the world.brand write gate live server-side
// in the grant. Draft images render through the existing room-member-media
// route's short-lived signed URL — re-resolved on expiry, never persisted.

export type BrandAppearance = "light" | "dark";
export type BrandMarkSlot = {
  readonly memberId: string | null;
  readonly contentSha256: string | null;
  readonly setAt: number | null;
};
const emptySlot: BrandMarkSlot = { memberId: null, contentSha256: null, setAt: null };
export const WORLD_BRAND_CHANGED_EVENT = "neucleos-world-brand-changed";

export type WorldBrandOutcome =
  | {
      readonly ok: true;
      readonly light: BrandMarkSlot;
      readonly dark: BrandMarkSlot;
      readonly memberId: string | null;
      readonly contentSha256: string | null;
      readonly setAt: number | null;
    }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

export type BrandMediaOutcome =
  | { readonly ok: true; readonly url: string; readonly expiresAt: number }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** True iff a member id names a brand-logo draft (the LG key grammar). */
export function isBrandLogoMemberId(memberId: string): boolean {
  return /^world-r2:context\/brand\/logo\/[0-9a-f]{64}\.png$/.test(memberId);
}

function refusal(response: Response, body: Record<string, unknown> | null): WorldBrandOutcome {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string" ? body["reason"] : "The World brand is unavailable.",
  };
}

function parsedBrand(body: Record<string, unknown>): WorldBrandOutcome | null {
  const memberId = body["memberId"];
  const contentSha256 = body["contentSha256"];
  const setAt = body["setAt"];
  if (memberId !== null && typeof memberId !== "string") return null;
  if (contentSha256 !== null && typeof contentSha256 !== "string") return null;
  if (setAt !== null && typeof setAt !== "number") return null;
  const parseSlot = (value: unknown): BrandMarkSlot | null => {
    if (value === undefined) return emptySlot;
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const b = value as Record<string, unknown>;
    return (b.memberId === null || typeof b.memberId === "string") &&
      (b.contentSha256 === null || typeof b.contentSha256 === "string") &&
      (b.setAt === null || typeof b.setAt === "number")
      ? (b as BrandMarkSlot)
      : null;
  };
  const light = parseSlot(body.light),
    dark = parseSlot(body.dark);
  if (!light || !dark) return null;
  return {
    ok: true,
    light,
    dark,
    memberId: (memberId as string | null) ?? null,
    contentSha256: (contentSha256 as string | null) ?? null,
    setAt: (setAt as number | null) ?? null,
  };
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<WorldBrandOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  const brand = parsedBrand(body);
  return (
    brand ?? {
      ok: false,
      kind: "transport",
      reason: "The local Cleo server returned an invalid World brand.",
    }
  );
}

export async function readWorldBrand(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldBrandOutcome> {
  return call("/api/neucleos/world/brand", { worldKey }, fetchImpl);
}

export async function setWorldLogo(
  worldKey: string,
  memberId: string | null,
  fetchImpl: typeof fetch = fetch,
  appearance?: BrandAppearance,
): Promise<WorldBrandOutcome> {
  const result = await call(
    "/api/neucleos/world/brand/set",
    { worldKey, memberId, ...(appearance ? { appearance } : {}) },
    fetchImpl,
  );
  if (result.ok)
    window.dispatchEvent(new CustomEvent(WORLD_BRAND_CHANGED_EVENT, { detail: worldKey }));
  return result;
}

/**
 * LG7 — the one-gesture upload: the lane uploads, sets, and reads back at
 * the authority; the answer is the post-set brand fold.
 */
export async function uploadWorldLogo(
  worldKey: string,
  pngBase64: string,
  fetchImpl: typeof fetch = fetch,
  appearance?: BrandAppearance,
): Promise<WorldBrandOutcome> {
  const result = await call(
    "/api/neucleos/world/brand/upload",
    { worldKey, pngBase64, ...(appearance ? { appearance } : {}) },
    fetchImpl,
  );
  if (result.ok)
    window.dispatchEvent(new CustomEvent(WORLD_BRAND_CHANGED_EVENT, { detail: worldKey }));
  return result;
}

/** Resolve one brand member to its short-lived signed URL (300s TTL). */
export async function resolveBrandMedia(
  worldKey: string,
  memberId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BrandMediaOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(
      "/api/neucleos/room-member-media",
      { worldKey, memberId },
      fetchImpl,
    );
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  const media = body?.["media"];
  if (
    body?.["ok"] === true &&
    typeof media === "object" &&
    media !== null &&
    typeof (media as Record<string, unknown>)["url"] === "string" &&
    typeof (media as Record<string, unknown>)["expiresAt"] === "number"
  ) {
    return {
      ok: true,
      url: (media as Record<string, unknown>)["url"] as string,
      expiresAt: (media as Record<string, unknown>)["expiresAt"] as number,
    };
  }
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? (body["kind"] as string) : "transport",
    reason:
      typeof body?.["reason"] === "string"
        ? (body["reason"] as string)
        : "The draft image could not be resolved.",
  };
}
